import { Box, Switch, type SwitchProps, Typography, styled } from '@mui/material';
import { useFormikContext } from 'formik';
import type { FC, ReactElement } from 'react';

import { SetFormikValue } from '@core/utils/formikHelpers';

import { Input, type InputProps } from '../Input/Input';

type StyledSwitchProps = SwitchProps & { disableStateColor?: boolean };

export const StyledSwitch: FC<StyledSwitchProps> = styled(
  ({ ...props }: StyledSwitchProps) => (
    <Switch focusVisibleClassName='.Mui-focusVisible' disableRipple {...props} />
  ),
  { shouldForwardProp: (prop) => prop !== 'disableStateColor' },
)<StyledSwitchProps>(({ theme, disableStateColor }) => ({
  '& .MuiSwitch-switchBase': {
    '&.Mui-checked': {
      color: theme.palette.SlabGray[0],
      '& + .MuiSwitch-track': {
        backgroundColor: theme.palette.SlabIndigo['500'],
      },
    },
  },
  '& .MuiSwitch-track': {
    backgroundColor: disableStateColor
      ? theme.palette.SlabIndigo['500']
      : theme.palette.SlabGray[400],
  },
}));

type InputSwitchProps = {
  switchFormikName: string;
  leftLabel: string;
  rightLabel: string;
  leftValue: string;
  rightValue: string;
  leftInputProps: Omit<InputProps, 'disabled'>;
  rightInputProps: Omit<InputProps, 'disabled'>;
  readOnly?: boolean;
  /** Rendered at the end of the toggle row (e.g. an inherited-value hint). */
  trailingContent?: ReactElement;
};

export const InputSwitch = ({
  switchFormikName,
  leftLabel,
  rightLabel,
  leftValue,
  rightValue,
  leftInputProps,
  rightInputProps,
  readOnly,
  trailingContent,
}: InputSwitchProps): ReactElement => {
  const formikBag = useFormikContext<any>();

  const switchFormikValue = formikBag.getFieldMeta(switchFormikName);
  const isSwitchChecked = switchFormikValue.value === rightValue;

  return (
    <Box display='flex' flexDirection='column' gap='0.5rem'>
      <Box display='flex' alignItems='center'>
        <label htmlFor={`${switchFormikName}.switch`}>
          <Typography fontWeight={isSwitchChecked ? 'normal' : 'bold'}>{leftLabel}</Typography>
        </label>
        <StyledSwitch
          disableStateColor
          disabled={readOnly}
          id={`${switchFormikName}.switch`}
          checked={isSwitchChecked}
          onClick={(): void => {
            if (isSwitchChecked) {
              SetFormikValue(formikBag, switchFormikName, leftValue);
            } else {
              SetFormikValue(formikBag, switchFormikName, rightValue);
            }
          }}
        />
        <label htmlFor={`${switchFormikName}.switch`}>
          <Typography fontWeight={isSwitchChecked ? 'bold' : 'normal'}>{rightLabel}</Typography>
        </label>
        {trailingContent !== undefined && <Box marginLeft='1rem'>{trailingContent}</Box>}
      </Box>
      <Box display='flex' flexDirection='row' gap='1rem'>
        <Input {...leftInputProps} disabled={isSwitchChecked} />
        <Input {...rightInputProps} disabled={!isSwitchChecked} />
      </Box>
    </Box>
  );
};
